import React, { createContext, useCallback, useContext, useState } from 'react'
import { useBoolean } from 'react-use'

import { FileCategory, IFilePresenter, IFolderBrief } from '@makyoapp/makyo-sdk/models/Files'
import { PaginatedItems } from '@makyoapp/makyo-common'

import { queryClient, queryKeys } from '@/lib/react-query'
import { useGetFolders, useGetStorage } from '@/resolvers/org/storage/query'
import { useQueryParam } from '@/shared/makyo-hooks'

import { useCurrentOrgContext } from './CurrentOrgContext'

interface StorageState {
  checks: IFilePresenter[]
  setChecks: (nextChecks: IFilePresenter[]) => void
  checkedFiles: IFilePresenter[]
  setCheckedFiles: (nextCheckedFiles: IFilePresenter[]) => void
  checkedFolders: IFolderBrief[]
  setCheckedFolders: (nextCheckedFolders: IFolderBrief[]) => void
  storage: PaginatedItems<IFilePresenter>
  isLoadingFiles: boolean
  isLoading: boolean
  search: string
  setSearch: (nextSearch: string) => void
  page: number
  setPage: (nextPage: number) => void
  resetParams: () => void
  loadingDelete: boolean
  setLoadingDelete: (next: boolean) => void
  activeCategory?: FileCategory
  setActiveCategory: (next?: FileCategory) => void
  isGridView: boolean
  setIsGridView: (next: boolean) => void
  preview?: IFilePresenter
  setPreview: (next?: IFilePresenter) => void
  refreshStorage: () => void
  currentFolderId: string
  setCurrentFolderId: (id: string) => void
  storageFolders: IFolderBrief[]
  isLoadingFolders: boolean
}

const StorageContext = createContext<StorageState | null>(null)

export function useStorageContext() {
  const context = useContext(StorageContext)
  if (!context) {
    throw new Error('useStorageContext must be used within a StorageProvider')
  }
  return context
}

/**
 * StorageProvider
 */
export const StorageProvider = ({ children }: { children?: React.ReactNode }) => {
  const { currentOrg } = useCurrentOrgContext()
  const [checks, setChecks] = useState<IFilePresenter[]>([])
  const [activeCategory, setActiveCategory] = useState<FileCategory | undefined>(undefined)
  const [isGridView, setIsGridView] = useBoolean(false)

  const { page, setPage, search, setSearch, resetParams } = useQueryParam()
  const [loadingDelete, setLoadingDelete] = useBoolean(false)
  const [preview, setPreview] = useState<undefined | IFilePresenter>(undefined)
  const [currentFolderId, setCurrentFolderId] = useState<string>('')

  const [checkedFiles, setCheckedFiles] = useState<IFilePresenter[]>([])
  const [checkedFolders, setCheckedFolders] = useState<IFolderBrief[]>([])

  // Access org storage folder
  const storageFoldersQuery = useGetFolders(currentFolderId)
  const storageFolders = storageFoldersQuery.data || []
  const isLoadingFolders = storageFoldersQuery.isLoading

  // Access Org Storage
  const storageQuery = useGetStorage({
    params: {
      page,
      search,
      categories: activeCategory ? [activeCategory] : [],
      folderId: currentFolderId || undefined,
    },
  })
  const storage = storageQuery.data || {
    items: [],
    totalCount: 0,
  }
  const isLoadingFiles = storageQuery.isLoading

  // refresh storage and metric after mutation
  // Make sure refreshStorage awaits query invalidation and forces a refetch
  const refreshStorage = useCallback(async () => {
    if (!currentOrg?.id) return

    // Invalidate queries first
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [queryKeys.orgStorage, currentOrg?.id],
      }),
      queryClient.invalidateQueries({
        queryKey: [queryKeys.orgStorageFolders, currentOrg?.id, currentFolderId],
      }),
      queryClient.invalidateQueries({
        queryKey: [queryKeys.orgStorageMetrics, currentOrg?.id],
      }),
    ])

    // Force refetch the storage query
    await storageQuery.refetch()
  }, [currentFolderId, storageQuery, currentOrg?.id])

  // derived state
  const isLoading = storageQuery.isLoading || storageFoldersQuery.isLoading || loadingDelete

  return (
    <>
      <StorageContext.Provider
        value={{
          checks,
          setChecks,
          checkedFiles,
          setCheckedFiles,
          checkedFolders,
          setCheckedFolders,
          storage,
          isLoadingFiles,
          isLoading,
          page,
          setPage,
          search,
          setSearch,
          resetParams,
          activeCategory,
          setActiveCategory,
          isGridView,
          setIsGridView,
          preview,
          setPreview,
          refreshStorage,
          loadingDelete,
          setLoadingDelete,
          currentFolderId,
          setCurrentFolderId,
          storageFolders,
          isLoadingFolders,
        }}
      >
        {children}
      </StorageContext.Provider>
    </>
  )
}
